/**
 * Theme preference (light / dark / auto), persisted per-device in
 * localStorage. Ported from ai-guidance's reference dashboard (same
 * mechanism, renamed storage key) — see that copy's header comment for why
 * this needs to run pre-paint. The <html data-theme> attribute is stamped in
 * two places that must stay in agreement:
 *   1. the inline boot script in index.html — pre-paint, no flash;
 *   2. applyTheme() here — when the user changes the setting, or the OS
 *      flips while on "auto" (watchSystemTheme).
 * If you change the storage key or the resolution rule, change the inline
 * script in index.html too.
 */

export type ThemePref = "light" | "dark" | "auto";

const THEME_KEY = "amh_theme";
const THEMES: ThemePref[] = ["light", "dark", "auto"];

const darkQuery = () => window.matchMedia("(prefers-color-scheme: dark)");

export function getThemePref(): ThemePref {
  const v = localStorage.getItem(THEME_KEY);
  return THEMES.includes(v as ThemePref) ? (v as ThemePref) : "light";
}

/** Resolve "auto" against the OS and stamp <html data-theme="light|dark">. */
export function applyTheme(pref: ThemePref): void {
  const dark = pref === "dark" || (pref === "auto" && darkQuery().matches);
  document.documentElement.dataset.theme = dark ? "dark" : "light";
}

export function setThemePref(pref: ThemePref): void {
  localStorage.setItem(THEME_KEY, pref);
  applyTheme(pref);
}

/** Follow the OS theme live, but only while the user's choice is "auto". */
export function watchSystemTheme(): void {
  darkQuery().addEventListener("change", () => {
    if (getThemePref() === "auto") applyTheme("auto");
  });
}
